<template>
  <public-layout>
    <div class="nf-page">

      <!-- Decorative grid -->
      <div class="nf-dots"></div>

      <div class="nf-inner">
        <!-- Large 404 -->
        <div class="nf-code" aria-hidden="true">404</div>

        <!-- Icon -->
        <div class="nf-icon">
          <svg width="64" height="64" fill="none" stroke="currentColor" stroke-width="1.2" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
            <path d="M11 8v4M11 15h.01" stroke-linecap="round"/>
          </svg>
        </div>

        <h1 class="nf-title">Page not found</h1>
        <p class="nf-sub">
          The page you're looking for doesn't exist or has been moved.<br>
          Let's get you back on track.
        </p>

        <!-- Quick links -->
        <div class="nf-actions">
          <a href="/" class="nf-btn nf-btn--primary">
            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            Go to Home
          </a>
          <a href="/listings" class="nf-btn nf-btn--secondary">
            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            Browse Listings
          </a>
          <button class="nf-btn nf-btn--ghost" @click="$router.back()">
            <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Go Back
          </button>
        </div>

        <!-- Quick nav links -->
        <div class="nf-links">
          <span class="nf-links__label">Explore:</span>
          <a href="/blog">Blog</a>
          <a href="/services">Services</a>
          <a href="/about">About</a>
          <a href="/contact">Contact</a>
        </div>
      </div>

    </div>
  </public-layout>
</template>

<script>
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO } from '@/utils/seo';

export default {
  name: 'NotFound',
  components: { PublicLayout },
  mounted() {
    useSEO({
      title: 'Page Not Found',
      description: "The page you're looking for doesn't exist. Browse Structurecity for construction listings, services, and more.",
      noindex: true,
    });
  },
};
</script>

<style scoped>
.nf-page {
  position: relative;
  min-height: calc(100vh - 68px);
  margin-top: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #0d1f36 0%, #1e3a5f 50%, #1a4a7a 100%);
  padding: 4rem 1.5rem;
  overflow: hidden;
}

.nf-dots {
  position: absolute; inset: 0; pointer-events: none;
  background-image: radial-gradient(circle, rgba(255,255,255,.06) 1px, transparent 1px);
  background-size: 36px 36px;
}

/* Glowing circle behind the 404 */
.nf-page::before {
  content: '';
  position: absolute;
  width: 560px; height: 560px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(245,158,11,.12) 0%, transparent 70%);
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.nf-inner {
  position: relative; z-index: 2;
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: 1.25rem; max-width: 560px;
}

/* Giant 404 */
.nf-code {
  font-size: clamp(7rem, 18vw, 12rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -.06em;
  background: linear-gradient(135deg, var(--accent, #f59e0b), #fbbf24);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  opacity: .9;
  margin-bottom: -.5rem;
  user-select: none;
}

/* Icon */
.nf-icon {
  width: 72px; height: 72px;
  border-radius: 50%;
  background: rgba(255,255,255,.08);
  border: 1.5px solid rgba(255,255,255,.15);
  display: flex; align-items: center; justify-content: center;
  color: rgba(255,255,255,.7);
  backdrop-filter: blur(8px);
}

.nf-title {
  font-size: clamp(1.5rem, 4vw, 2.25rem);
  font-weight: 800;
  color: #fff;
  margin: 0;
  letter-spacing: -.03em;
}

.nf-sub {
  font-size: 1.0625rem;
  color: rgba(255,255,255,.6);
  line-height: 1.65;
  margin: 0;
}

/* Action buttons */
.nf-actions {
  display: flex; align-items: center;
  gap: .75rem; flex-wrap: wrap;
  justify-content: center;
  margin-top: .5rem;
}

.nf-btn {
  display: inline-flex; align-items: center; gap: .4rem;
  padding: .625rem 1.375rem;
  border-radius: var(--radius-full, 9999px);
  font-size: .9375rem; font-weight: 700;
  cursor: pointer; text-decoration: none;
  border: none; font-family: inherit;
  transition: transform .15s, box-shadow .15s, background .15s, opacity .15s;
}
.nf-btn:hover { transform: translateY(-1px); text-decoration: none; }

.nf-btn--primary {
  background: var(--accent, #f59e0b); color: #0d1f36;
  box-shadow: 0 4px 14px rgba(245,158,11,.35);
}
.nf-btn--primary:hover { background: #fbbf24; box-shadow: 0 6px 18px rgba(245,158,11,.45); color: #0d1f36; }

.nf-btn--secondary {
  background: rgba(255,255,255,.12);
  color: #fff;
  border: 1.5px solid rgba(255,255,255,.2);
  backdrop-filter: blur(8px);
}
.nf-btn--secondary:hover { background: rgba(255,255,255,.2); color: #fff; }

.nf-btn--ghost {
  background: none; color: rgba(255,255,255,.6);
  border: 1.5px solid rgba(255,255,255,.12);
}
.nf-btn--ghost:hover { color: #fff; border-color: rgba(255,255,255,.3); }

/* Quick nav */
.nf-links {
  display: flex; align-items: center;
  gap: .875rem; flex-wrap: wrap;
  justify-content: center;
  margin-top: .25rem;
  font-size: .875rem;
}
.nf-links__label { color: rgba(255,255,255,.4); font-weight: 500; }
.nf-links a {
  color: rgba(255,255,255,.6); text-decoration: none;
  font-weight: 500; transition: color .15s;
}
.nf-links a:hover { color: #fff; }

@media (max-width: 480px) {
  .nf-actions { flex-direction: column; width: 100%; }
  .nf-btn { width: 100%; justify-content: center; }
}
</style>
